import { Input } from '@populix/pop-ui'
import React, { useCallback, useEffect } from 'react'

import { SearchableSelect, PageLoading } from '@/components'
import { useGetDomicileData } from '@/libs/hooks/use-get-domicile-data'

type TDomicile = {
  readonly values: string[]
  readonly onChange: (value: string[]) => void
  readonly error?: string
  readonly disabled?: boolean
}

export default function Domicile({ values, onChange, error, disabled }: TDomicile) {
  const {
    dataProvince,
    isLoadingProvince,
    dataCity,
    isLoadingCity,
    dataDistrict,
    isLoadingDistrict,
    dataSubdistrict,
    isLoadingSubdistrict,
    dataZipCode,
    isLoadingZipCode,
  } = useGetDomicileData(values)

  const handleOnChange = ({ value, state }: { value: string; state: number }) => {
    const updatedValues = [...values]
    updatedValues[state] = value

    onChange(
      updatedValues.map((res, index) => {
        if (index > state) {
          return ''
        }
        return res
      }),
    )

    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
    }, 100)
  }

  const handleOnChangeZipCode = useCallback(() => {
    if (dataZipCode) {
      const zipCode = dataZipCode?.result?.data?.[0]
      const updatedValues = [...values]
      updatedValues[4] = zipCode

      onChange(updatedValues)

      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataZipCode])

  useEffect(() => {
    handleOnChangeZipCode()
  }, [dataZipCode, handleOnChangeZipCode])

  const isFetching =
    isLoadingProvince ||
    isLoadingCity ||
    isLoadingDistrict ||
    isLoadingSubdistrict ||
    isLoadingZipCode

  return (
    <div className='flex flex-col gap-4'>
      <PageLoading show={isFetching} />

      <SearchableSelect
        caption='Provinsi'
        placeholder='Select Province'
        value={values[0]}
        onChange={(e) => handleOnChange({ value: e, state: 0 })}
        options={
          dataProvince?.result?.data?.map((res) => ({
            label: res,
            value: res,
          })) ?? []
        }
        error={!!error && !values?.[0] ? error : ''}
      />

      {values[0] && (
        <SearchableSelect
          caption='Kota/Kabupaten'
          placeholder='Select City/District'
          value={values[1]}
          onChange={(e) => handleOnChange({ value: e, state: 1 })}
          disabled={disabled}
          options={
            dataCity?.result?.data?.map((res) => ({
              label: res,
              value: res,
            })) ?? []
          }
          error={!!error && !values?.[1] ? error : ''}
        />
      )}

      {values[1] && (
        <SearchableSelect
          caption='Kecamatan'
          placeholder='Select District'
          value={values[2]}
          disabled={disabled}
          onChange={(e) => handleOnChange({ value: e, state: 2 })}
          options={
            dataDistrict?.result?.data?.map((res) => ({
              label: res,
              value: res,
            })) ?? []
          }
          error={!!error && !values?.[2] ? error : ''}
        />
      )}

      {values[2] && (
        <SearchableSelect
          caption='Kelurahan'
          placeholder='Select Subdistrict'
          value={values[3]}
          onChange={(e) => handleOnChange({ value: e, state: 3 })}
          options={
            dataSubdistrict?.result?.data?.map((res) => ({
              label: res,
              value: res,
            })) ?? []
          }
          error={!!error && !values?.[3] ? error : ''}
          disabled={!values[2] || disabled}
        />
      )}

      {values[3] && (
        <Input
          caption='Kode Pos'
          label='Enter Postal Code'
          value={values[4]}
          type='tel'
          onChange={(e) => handleOnChange({ value: e.target.value, state: 4 })}
          error={!!error && !values?.[4] ? error : ''}
          disabled
        />
      )}
    </div>
  )
}
